import { History } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { useAuditTimeline } from '../queries';
import type { AuditEntry } from '../types';

interface AuditTimelinePanelProps {
  /** Django-style "app_label.model" — e.g. "crm.deal". */
  entity: string;
  /** Target's PK. */
  entityId: string | number;
  /** Optional override for the card heading. */
  title?: string;
}

/**
 * Read-only activity feed for one entity record. Calls the per-target audit
 * endpoint which any staff member can read once both filters are supplied.
 */
export function AuditTimelinePanel({ entity, entityId, title }: AuditTimelinePanelProps) {
  const { data, isPending, isError } = useAuditTimeline(entity, entityId);
  const entries = data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="size-4" /> {title ?? 'History'}
        </CardTitle>
        <CardDescription>
          Audited changes to this {entity.split('.').pop()}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : isError ? (
          <p className="text-muted-foreground text-sm">
            Couldn’t load activity. Please try again.
          </p>
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No activity yet.</p>
        ) : (
          <ol className="space-y-3">
            {entries.map((entry) => (
              <TimelineRow key={entry.id} entry={entry} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function TimelineRow({ entry }: { entry: AuditEntry }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="bg-primary/70 mt-1.5 inline-block size-2 shrink-0 rounded-full"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-medium">{entry.actor_name ?? 'System'}</span>{' '}
          <span className="text-muted-foreground">{phraseFor(entry)}</span>
        </p>
        <p className="text-muted-foreground text-xs">
          {new Date(entry.created).toLocaleString()}
        </p>
      </div>
    </li>
  );
}

/** "crm.deal.stage_changed" → "stage changed" (lowercased, underscores stripped). */
function humanize(code: string): string {
  const tail = code.split('.').pop() ?? code;
  return tail.replace(/_/g, ' ');
}

function asText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  return JSON.stringify(v);
}

/**
 * Sentence builder for the action codes the BE currently emits. Falls back
 * to the humanized code so unknown actions still render readably.
 */
function phraseFor(entry: AuditEntry): string {
  const { action, before, after } = entry;

  switch (action) {
    case 'crm.lead.converted':
      return 'converted this lead to a deal';
    case 'crm.deal.stage_changed':
      return `moved this deal from ${asText(before.stage)} to ${asText(after.stage)}`;
    case 'crm.proposal.accepted':
      return 'accepted this proposal';
    default:
      break;
  }

  if (action.endsWith('.archived')) return 'archived this record';
  if (action.endsWith('.restored')) return 'restored this record';
  if (action.endsWith('.created')) return 'created this record';
  if (action.endsWith('.updated')) {
    const fields = Object.keys(after ?? {}).map((f) => f.replace(/_/g, ' '));
    return fields.length ? `updated ${fields.join(', ')}` : 'updated this record';
  }

  return humanize(action);
}
